import {
  type TicketMessageCitation,
  dedupeGraphCitations,
  dedupeRuleCitations,
} from "./commentSourceUtils";

/** The one message whose citations the Cited claims panel is showing. */
export type FocusedCitedClaims = {
  messageId: string;
  citations: TicketMessageCitation[];
};

/** A message as far as this selection cares: role, whether it has landed, and
 * what it cited. Structural on purpose so the route's fuller message type
 * satisfies it without this module importing the route. */
type CitableMessage = {
  _id: string;
  role?: string;
  content?: unknown;
  status?: string;
  citations?: readonly TicketMessageCitation[];
};

/** Citations from one message, in the order the panel renders them: rule chips
 * first, then the graph relationships behind them.
 *
 * why: the two are different shapes and `dedupeRuleCitations` filters graph
 * citations out. Deriving the panel's input from rules alone silently drops
 * every graph relationship the answer was built on.
 */
export function citationsForPanel(
  message: CitableMessage,
): TicketMessageCitation[] {
  const raw = message.citations ?? [];
  return [...dedupeRuleCitations(raw), ...dedupeGraphCitations(raw)];
}

/** Which message the panel should show.
 *
 * A clicked chip pins its own message (`focusedMessageId`); otherwise the most
 * recent assistant message that actually cited something wins, so the panel
 * tracks the conversation instead of emptying on a turn with no citations.
 * A pending message with no content yet is skipped - its citations arrive with
 * the content, and showing it early empties the panel mid-stream.
 */
export function selectFocusedCitedClaims(
  messages: readonly CitableMessage[],
  focusedMessageId?: string | null,
): FocusedCitedClaims | null {
  const pick = (message: CitableMessage): FocusedCitedClaims | null => {
    if (message.role !== "assistant") return null;
    if (!message.content && message.status === "pending") return null;
    const citations = citationsForPanel(message);
    if (citations.length === 0) return null;
    return { messageId: message._id, citations };
  };

  if (focusedMessageId) {
    const targeted = messages.find((m) => m._id === focusedMessageId);
    if (targeted) {
      const hit = pick(targeted);
      if (hit) return hit;
    }
  }
  for (let i = messages.length - 1; i >= 0; i--) {
    const hit = pick(messages[i]);
    if (hit) return hit;
  }
  return null;
}

/** The focused answer's citations without the rules it only matched.
 *
 * why: the legacy chat's panel is titled "Cited claims" and has no related
 * group, so a `related` rule there contradicts the chip row under the answer,
 * which counts it as "+n related".
 */
export function withoutRelatedClaims(
  focused: FocusedCitedClaims | null,
): FocusedCitedClaims | null {
  if (!focused) return null;
  const citations = focused.citations.filter(
    (citation) => !("related" in citation && citation.related === true),
  );
  return citations.length > 0 ? { ...focused, citations } : null;
}
